import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useNavigationStore from './navigationStore';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

export type ExternalLinksState = {
  pendingChanges: boolean;
  saveStatus: SaveStatus;
  discardRequested: boolean;

  setPendingChanges: (isDirty: boolean) => void;
  requestSave: () => void;
  requestDiscard: () => void;
  completeSave: (success: boolean) => void;
  clearDiscardRequest: () => void;
};

const registerExternalLinksDialog = (get: () => ExternalLinksState): void => {
  useNavigationStore.getState().registerDialogConfig('externalLinks', {
    titleKey: 'common:externalLinks.unsavedDialog.title',
    messageKey: 'common:externalLinks.unsavedDialog.message',
    secondaryLabelKey: 'common:externalLinks.unsavedDialog.discardButton',
    primaryLabelKey: 'common:externalLinks.unsavedDialog.saveButton',
    onSecondary: () => get().requestDiscard(),
    onPrimary: () => get().requestSave(),
    isPrimaryAsync: true,
  });
};

const useExternalLinksStore = create<ExternalLinksState>()(
  devtools(
    (set, get) => ({
      pendingChanges: false,
      saveStatus: 'idle',
      discardRequested: false,

      setPendingChanges: (isDirty: boolean) => {
        set({ pendingChanges: isDirty }, false, 'setPendingChanges');
        if (isDirty) {
          registerExternalLinksDialog(get);
        }
        useNavigationStore.getState().setPendingChanges('externalLinks', isDirty);
      },

      requestSave: () => {
        set({ saveStatus: 'saving' }, false, 'requestSave');
        useNavigationStore
          .getState()
          .updateDialogConfig('externalLinks', { isPrimaryLoading: true });
      },

      requestDiscard: () => {
        set({ discardRequested: true, pendingChanges: false }, false, 'requestDiscard');
        useNavigationStore.getState().setPendingChanges('externalLinks', false);
      },

      completeSave: (success: boolean) => {
        set(
          {
            saveStatus: success ? 'success' : 'error',
            ...(success && { pendingChanges: false }),
          },
          false,
          'completeSave',
        );
        useNavigationStore
          .getState()
          .updateDialogConfig('externalLinks', { isPrimaryLoading: false });
        if (success) {
          useNavigationStore.getState().setPendingChanges('externalLinks', false);
        }
      },

      clearDiscardRequest: () => {
        set({ discardRequested: false }, false, 'clearDiscardRequest');
      },
    }),
    { name: 'ExternalLinksStore' },
  ),
);

export default useExternalLinksStore;
